export const TYPE_LABELS: Record<string, string> = {
  festival: "Festival",
  vrat: "Vrat",
  eclipse: "Eclipse",
  tithi_based: "Tithi-based",
};

// Hindi twin of TYPE_LABELS — the type badge on /hi/calendar/festival/<id>/.
export const TYPE_LABELS_HI: Record<string, string> = {
  festival: "पर्व",
  vrat: "व्रत",
  eclipse: "ग्रहण",
  tithi_based: "तिथि-आधारित",
};

// Presentation-only badge roles. Festival facts remain in festivals.yaml and the
// generated fact modules; these values only select existing semantic utilities.
export const TYPE_BADGE_CLASSES: Record<string, string> = {
  festival: "border-warning/30 bg-warning/10 text-warning",
  vrat: "border-success/30 bg-success/10 text-success",
  eclipse: "border-intent-brand/30 bg-surface-brand-subtle text-intent-brand",
  tithi_based: "border-info/30 bg-info/10 text-info",
};

export function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface FestivalFaqSource {
  name_en: string;
  name_hi: string;
  date_2026: string;
  date_2027: string;
  fasting: boolean;
}

interface FestivalFaq {
  question: string;
  answer: string;
}

function englishDateFaq(name: string, date2026: string, date2027: string): FestivalFaq | null {
  if (!date2026 && !date2027) return null;
  if (date2026 && date2027) {
    return {
      question: `When is ${name} celebrated?`,
      answer: `${name} is celebrated on ${date2026} in 2026 and ${date2027} in 2027. The date changes each year because it follows the Hindu lunar calendar (Panchang) — every date here is computed from real planetary positions using the Swiss Ephemeris engine, not copied from a fixed list.`,
    };
  }
  return {
    question: `When is ${name} celebrated?`,
    answer: `${name} is celebrated on ${date2026 || date2027}. The date follows the Hindu lunar calendar (Panchang) and is computed from real planetary positions using the Swiss Ephemeris engine.`,
  };
}

function hindiDateFaq(name: string, date2026: string, date2027: string): FestivalFaq | null {
  if (!date2026 && !date2027) return null;
  if (date2026 && date2027) {
    return {
      question: `${name} कब है?`,
      answer: `${name} 2026 में ${date2026} और 2027 में ${date2027} को है। यह तिथि हर वर्ष हिंदू पंचांग के अनुसार बदलती है — यहाँ दी गई हर तिथि स्विस एफ़ेमेरिस इंजन से वास्तविक ग्रह स्थिति के आधार पर गणना की जाती है, किसी सामान्य सूची से नहीं ली जाती।`,
    };
  }
  return {
    question: `${name} कब है?`,
    answer: `${name} ${date2026 || date2027} को है। यह तिथि हिंदू पंचांग के अनुसार तय होती है और स्विस एफ़ेमेरिस इंजन से वास्तविक ग्रह स्थिति के आधार पर गणना की जाती है।`,
  };
}

function englishFastingFaq(name: string, fasting: boolean): FestivalFaq {
  return {
    question: `Is ${name} a fasting (vrat) day?`,
    answer: fasting
      ? `Yes — ${name} is observed as a vrat (fasting day). Devotees keep a fast and complete the puja before breaking it, following the puja vidhi for the day.`
      : `${name} is not primarily a fasting day, though many families still keep a light fast or a sattvic diet as part of the observance.`,
  };
}

function hindiFastingFaq(name: string, fasting: boolean): FestivalFaq {
  return {
    question: `क्या ${name} व्रत का दिन है?`,
    answer: fasting
      ? `हाँ — ${name} व्रत (उपवास) के रूप में रखा जाता है। भक्त उपवास रखते हैं और पूजा विधि पूरी करने के बाद ही व्रत खोलते हैं।`
      : `${name} मुख्य रूप से व्रत का दिन नहीं है, फिर भी कई परिवार इस दिन सात्विक आहार या हल्का उपवास रखते हैं।`,
  };
}

/**
 * Festival FAQ — the single source for BOTH the visible FAQ render (FestivalBody)
 * and the FAQPage JSON-LD (`pageSeoJsonLd({ faqs })` on the en + hi festival
 * pages). One function → identical question/answer text on both surfaces, so the
 * schema can never drift from the visible copy (rule 49 faq_parity; rules 23/24
 * "drifting copies").
 *
 * Every fact is ENGINE-CANONICAL: the dates come from `festival.date_20XX` via
 * `formatDate` (a structured field, never an LLM/literal date — rule 24) and
 * fasting from the boolean. Significance stays in its dedicated visible section;
 * repeating that same backend prose as an FAQ answer creates duplicate body copy.
 * The answers are "answer-first" because festival "when is X / is X a vrat"
 * queries are exactly the zero-click queries this cluster must remain the cited
 * SOURCE for.
 */
export function festivalFaqs(
  festival: FestivalFaqSource,
  locale: "en" | "hi" = "en"
): FestivalFaq[] {
  const isHindi = locale === "hi";
  const name = isHindi ? festival.name_hi : festival.name_en;
  const date2026 = festival.date_2026 ? formatDate(festival.date_2026) : "";
  const date2027 = festival.date_2027 ? formatDate(festival.date_2027) : "";
  const dateFaq = isHindi
    ? hindiDateFaq(name, date2026, date2027)
    : englishDateFaq(name, date2026, date2027);
  const fastingFaq = isHindi
    ? hindiFastingFaq(name, festival.fasting)
    : englishFastingFaq(name, festival.fasting);

  return dateFaq ? [dateFaq, fastingFaq] : [fastingFaq];
}
